import { useState, useEffect, useContext } from 'react';
import { Container, Paper, Box, Grid } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFullDialog,
    SttFormTextarea,
    SttButton,
    SttButtonCancel,
    SttList,
    UploadFileDialog,
} from '../../../sporttia/all';
import Receptors from './Receptors';
import { deleteElementFromArray } from '../../../lib/utils';
import NewMessageResultDialog from './NewMessageResultDialog';

type Props = {
    open?: boolean;
    onClose: () => void;
};

export default function NewMessageDialog({
    open = false,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [text, setText] = useState<string>('');
    const [shortText, setShortText] = useState<string>('');
    const [sendAllSC, setSendAllSC] = useState<boolean>(false);
    const [_SMSCount, setSMSCount] = useState<number>(0);
    const [receptors, setReceptors] = useState<any[]>([]); // {caption, user, group}
    const [uploadFileOpen, setUploadFileOpen] = useState<boolean>(false);
    const [attachedFiles, setAttachedFiles] = useState<any[]>([]);

    // Sent
    const [messageSent, setMessageSent] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>();

    /**
     * Send message
     */
    const sendMessage = () => {
        const groups = receptors
            .filter((r: any) => r.group !== undefined)
            .map((r: any) => r.group.id);

        const users = receptors
            .filter((r: any) => r.mship !== undefined)
            .map((r: any) => ({
                id: r.mship.user.id,
                name: r.mship.user.name,
            }));

        // Call API
        cxt?.api('POST', '/messages', {
            params: {
                text,
                short: shortText,
                scs: sendAllSC ? [cxt?.sc?.id] : null,
                groups: !sendAllSC ? groups : null,
                users: !sendAllSC ? users : null,
                files: attachedFiles.map((file: any) => file.id),
            },
            success: (r: any) => {
                setMessageSent(true);
                setErrors(r.errors);
            },
        });
    };

    /**
     * Clean all
     */
    const cleanAll = () => {
        setText('');
        setShortText('');
        setReceptors([]);
        setAttachedFiles([]);
    };

    /**
     * Add receptor
     */
    const addReceptor = (receptor: any) => {
        setReceptors([...receptors, receptor]);
    };

    /**
     * Delete receptor
     */
    const delReceptor = (receptor: any) => {
        setReceptors(deleteElementFromArray(receptors, receptor) ?? []);
    };

    /**
     * Short text changes
     */
    useEffect(() => {
        setSMSCount(shortText.length);
    }, [shortText]);

    /**
     * Open this dialog
     */
    useEffect(() => {
        if (open) cleanAll();
    }, [open]);

    // -----| Render |-----
    return (
        <>
            <SttFullDialog
                title={t('Message')}
                open={open}
                onClose={onClose}
                buttons={[
                    {
                        caption: t('CleanAll'),
                        icon: <FlipCameraAndroidIcon />,
                        onClick: () => cleanAll(),
                    },
                ]}
            >
                <Container>
                    <Box mt={3}>
                        <Grid container spacing={3}>
                            <Grid size={{ md: 8, sm: 8, xs: 12 }}>
                                <Paper>
                                    <Box p={3}>
                                        <SttFormTextarea
                                            rows={13}
                                            helperText={t('sc.user.emailText')}
                                            caption={t('Text')}
                                            defVal={text}
                                            onChange={({
                                                value,
                                            }: {
                                                name: string | undefined;
                                                value: unknown;
                                            }) => setText(value as string)}
                                        />

                                        {/* <Box mt={2}>
											<SttFormTextarea
												rows={3}
												helperText={ t('sc.user.smsText') }
												caption={`SMS / Push Notification - [ ${SMSCount} / 160 ]`}
												defVal={shortText}
												onChange={({ value }: any) => setShortText(value)}
											/>
										</Box> */}
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid size={{ md: 4, sm: 4, xs: 12 }}>
                                <Receptors
                                    receptors={receptors}
                                    sendAllSC={sendAllSC}
                                    setSendAllSC={setSendAllSC}
                                    onAddReceptor={addReceptor}
                                    onDelReceptor={delReceptor}
                                />

                                <Box mt={2}>
                                    <SttList
                                        paper
                                        title={t('Attached')}
                                        onAdd={() => setUploadFileOpen(true)}
                                        onDelete={(file: any) =>
                                            setAttachedFiles(
                                                deleteElementFromArray(
                                                    attachedFiles,
                                                    file,
                                                ) ?? [],
                                            )
                                        }
                                        data={attachedFiles.map(
                                            (file: any) => ({
                                                caption: file.name,
                                                objToSelect: file,
                                            }),
                                        )}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        <Box mt={3} display="flex" gap={2}>
                            <SttButton
                                variant="contained"
                                color="primary"
                                endIcon={<SendIcon />}
                                onClick={() => sendMessage()}
                            >
                                {t('Send')}
                            </SttButton>

                            <SttButtonCancel onClick={() => onClose()} />
                        </Box>
                    </Box>
                </Container>
            </SttFullDialog>

            <NewMessageResultDialog
                open={messageSent}
                errors={errors}
                onClose={() => {
                    setMessageSent(false);
                    cleanAll();
                }}
            />

            <UploadFileDialog
                open={uploadFileOpen}
                types={['SC'] as any}
                onClose={() => setUploadFileOpen(false)}
                onUploaded={(r: any) => {
                    const { file } = r;
                    setUploadFileOpen(false);
                    setAttachedFiles([...attachedFiles, file]);
                }}
            />
        </>
    );
}
